import React, { useState } from "react";
import { createPortal } from "react-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { cdnImage } from "../lib/cdn";
import Confetti from "./Confetti";

const REWARD_URL = "https://luma.com/hackaton?coupon=0T24BJ";
const REWARD_COUPON = "0T24BJ";
const SHARED_AT_KEY = "sa_shared_at";

const markShared = () => {
    try {
        window.localStorage.setItem(SHARED_AT_KEY, String(Date.now()));
    } catch {
        // localStorage unavailable, banner just won't fire — harmless
    }
};

const SHORT_CODE_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const SHORT_CODE_LENGTH = 6;

const generateShortCode = () => {
    const buf = new Uint8Array(SHORT_CODE_LENGTH);
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(buf);
    } else {
        for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    }
    let out = "";
    for (let i = 0; i < buf.length; i++) {
        out += SHORT_CODE_ALPHABET[buf[i] % SHORT_CODE_ALPHABET.length];
    }
    return out;
};

const mintShortLink = async ({ postId, platform, memberId, campaign }) => {
    for (let attempt = 0; attempt < 3; attempt++) {
        const code = generateShortCode();
        try {
            const payload = {
                postId,
                platform,
                createdAt: serverTimestamp(),
            };
            if (memberId) payload.memberId = memberId;
            if (campaign && typeof campaign === "string" && /^[a-z0-9-]{1,32}$/.test(campaign)) {
                payload.campaign = campaign;
            }
            await setDoc(doc(db, "links", code), payload);
            return code;
        } catch (err) {
            if (attempt === 2) throw err;
        }
    }
    return null;
};

const readMemberId = () => {
    try {
        return window.localStorage.getItem("ambasada.memberId") || null;
    } catch {
        return null;
    }
};

const PLATFORM_LABELS = {
    facebook: 'Share to Facebook',
    x: 'Share to X',
    threads: 'Share to Threads',
    whatsapp: 'Share to WhatsApp',
    linkedin: 'Share to LinkedIn',
    instagram: 'Share to Instagram',
    tiktok: 'Share to TikTok',
};

const PLATFORM_DISPLAY_NAME = {
    facebook: 'Facebook',
    x: 'X',
    threads: 'Threads',
    whatsapp: 'WhatsApp',
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    tiktok: 'TikTok',
};

// Where to send the user on desktop after they've downloaded the image — a page
// where they can create a new post and attach the image they just downloaded.
const PLATFORM_COMPOSE_URL = {
    facebook: 'https://www.facebook.com/',
    x: 'https://x.com/compose/post',
    linkedin: 'https://www.linkedin.com/feed/?shareActive=true',
    threads: 'https://www.threads.net/',
    whatsapp: 'https://web.whatsapp.com/',
    instagram: 'https://www.instagram.com/',
    tiktok: 'https://www.tiktok.com/upload',
};

const PLATFORM_ICONS = {
    instagram: (
        <svg width="40" height="40" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.9749 35.996C8.03347 35.996 0 28.1545 0 18.4507C0 8.74692 8.08368 0.856445 18.0251 0.856445C27.9163 0.856445 36 8.74692 36 18.4507C36 28.1545 27.9163 35.996 17.9749 35.996Z" fill="url(#paint0_radial_3539_13140)"/>
            <path d="M26.942 14.8274C26.9319 14.0897 26.7902 13.3593 26.5232 12.6689C26.2917 12.0866 25.938 11.5577 25.4848 11.1161C25.0317 10.6745 24.489 10.3299 23.8914 10.1043C23.192 9.84845 22.4532 9.71012 21.7064 9.69517C20.7448 9.65329 20.4399 9.6416 17.999 9.6416C15.5581 9.6416 15.2452 9.6416 14.2906 9.69517C13.5441 9.71023 12.8057 9.84855 12.1066 10.1043C11.5089 10.3298 10.9661 10.6743 10.513 11.1159C10.0598 11.5576 9.70618 12.0865 9.47479 12.6689C9.21174 13.3499 9.07009 14.0697 9.05598 14.7972C9.01299 15.7352 9 16.0323 9 18.4109C9 20.7895 9 21.0934 9.05598 22.0246C9.07097 22.7532 9.21191 23.472 9.47479 24.1548C9.70656 24.7371 10.0604 25.2658 10.5138 25.7072C10.9671 26.1487 11.5099 26.4931 12.1076 26.7185C12.8047 26.9846 13.5434 27.1329 14.2916 27.1568C15.2542 27.1987 15.5591 27.2114 18 27.2114C20.4409 27.2114 20.7538 27.2114 21.7084 27.1568C22.4552 27.1425 23.1941 27.0045 23.8934 26.7487C24.4908 26.5228 25.0334 26.1781 25.4865 25.7365C25.9396 25.295 26.2934 24.7662 26.5252 24.1841C26.7881 23.5022 26.929 22.7834 26.944 22.0538C26.987 21.1168 27 20.8197 27 18.4401C26.998 16.0615 26.998 15.7596 26.942 14.8274ZM17.993 22.909C15.4401 22.909 13.3721 20.8937 13.3721 18.406C13.3721 15.9183 15.4401 13.903 17.993 13.903C19.2186 13.903 20.3939 14.3775 21.2605 15.2219C22.1271 16.0664 22.614 17.2118 22.614 18.406C22.614 19.6003 22.1271 20.7457 21.2605 21.5901C20.3939 22.4346 19.2186 22.909 17.993 22.909ZM22.7979 14.7865C22.6563 14.7866 22.5162 14.7596 22.3854 14.7068C22.2546 14.6541 22.1357 14.5768 22.0357 14.4792C21.9356 14.3817 21.8562 14.2659 21.8021 14.1385C21.748 14.011 21.7202 13.8744 21.7203 13.7365C21.7203 13.5986 21.7482 13.4622 21.8023 13.3348C21.8565 13.2075 21.9358 13.0918 22.0358 12.9943C22.1358 12.8969 22.2545 12.8196 22.3852 12.7668C22.5159 12.7141 22.6559 12.6869 22.7974 12.6869C22.9388 12.6869 23.0789 12.7141 23.2095 12.7668C23.3402 12.8196 23.4589 12.8969 23.5589 12.9943C23.6589 13.0918 23.7383 13.2075 23.7924 13.3348C23.8465 13.4622 23.8744 13.5986 23.8744 13.7365C23.8744 14.317 23.3926 14.7865 22.7979 14.7865Z" fill="white"/>
            <path d="M17.9939 21.3315C19.6516 21.3315 20.9955 20.022 20.9955 18.4065C20.9955 16.791 19.6516 15.4814 17.9939 15.4814C16.3361 15.4814 14.9922 16.791 14.9922 18.4065C14.9922 20.022 16.3361 21.3315 17.9939 21.3315Z" fill="white"/>
            <defs>
                <radialGradient id="paint0_radial_3539_13140" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(-0.912202 36.1328) scale(66.5852 64.9937)">
                    <stop stopColor="#F4EC55"/>
                    <stop offset="0.2412" stopColor="#E48242"/>
                    <stop offset="0.3463" stopColor="#D66863"/>
                    <stop offset="0.4422" stopColor="#CD4F74"/>
                    <stop offset="0.4888" stopColor="#C94379"/>
                    <stop offset="1" stopColor="#3F56A6"/>
                </radialGradient>
            </defs>
        </svg>
    ),
    facebook: (
        <svg width="40" height="40" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 17.4023C0 8.23786 7.61118 0.808594 17 0.808594C26.3888 0.808594 34 8.23786 34 17.4023C34 26.5667 26.3888 33.9959 17 33.9959C7.61118 33.9959 0 26.5667 0 17.4023Z" fill="#3B5998"/>
            <path d="M18.0245 25.6991V17.4013H20.3712L20.6822 14.5419H18.0245L18.0286 13.1107C18.0286 12.3648 18.1011 11.9653 19.1986 11.9653H20.6656V9.10547H18.3186C15.4995 9.10547 14.5073 10.4926 14.5073 12.8254V14.5422H12.75V17.4016H14.5073V25.6991H18.0245Z" fill="white"/>
        </svg>
    ),
    x: (
        <svg width="40" height="40" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="17.5" cy="17.8903" rx="17.5" ry="17.0817" fill="#111827"/>
            <path d="M12.4273 10.2031H9.774L15.5707 16.67L8.75134 25.4699H14.0908L18.2729 20.1328L23.0581 25.4699H25.713L19.5129 18.5529L26.0547 10.2031H20.5797L16.7994 15.0815L12.4273 10.2031ZM13.3585 23.9198H11.8883L21.3785 11.6719H22.9562L13.3585 23.9198Z" fill="white"/>
        </svg>
    ),
    linkedin: (
        <svg width="40" height="40" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="17" cy="17.4023" rx="17" ry="16.5937" fill="#2D64BC"/>
            <path d="M13.2831 13.6035H10.1055V22.9332H13.2831V13.6035Z" fill="white"/>
            <path d="M11.7155 9.10547C10.6283 9.10547 9.91797 9.80313 9.91797 10.7175C9.91797 11.6127 10.6067 12.3296 11.6734 12.3296H11.6939C12.8019 12.3296 13.4916 11.6127 13.4916 10.7175C13.471 9.80313 12.802 9.10547 11.7155 9.10547Z" fill="white"/>
            <path d="M21.0869 13.3848C19.4014 13.3848 18.6464 14.2896 18.2237 14.9251V13.6042H15.0469C15.089 14.4793 15.0469 22.9338 15.0469 22.9338H18.2236V17.7234C18.2236 17.4446 18.2442 17.1657 18.3284 16.9664C18.5577 16.4095 19.0807 15.8325 19.9584 15.8325C21.1075 15.8325 21.5678 16.6884 21.5678 17.942V22.9338H24.7447V17.5836C24.7447 14.7179 23.1772 13.3848 21.0869 13.3848Z" fill="white"/>
        </svg>
    ),
    threads: (
        <svg width="40" height="40" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M36 18C36 27.9411 27.9411 36 18 36C8.05887 36 0 27.9411 0 18C0 8.05887 8.05887 0 18 0C27.9411 0 36 8.05887 36 18Z" fill="black"/>
            <path d="M22.1295 17.2845C22.2207 17.3222 22.3036 17.3642 22.3907 17.402C23.6017 17.9939 24.4891 18.8797 24.9536 19.9796C25.6047 21.5118 25.6669 24.0012 23.697 25.991C22.1958 27.5107 20.367 28.195 17.7834 28.216H17.771C14.8598 28.195 12.6245 27.2043 11.1192 25.269C9.77969 23.5478 9.09129 21.1508 9.06641 18.1492V18.1366V18.1282C9.08714 15.1267 9.77554 12.7297 11.115 11.0085C12.6204 9.07323 14.8598 8.08251 17.771 8.06152H17.7834C20.6988 8.08251 22.963 9.06904 24.514 10.9959C25.277 11.9488 25.841 13.0949 26.1977 14.4256L24.5223 14.879C24.2279 13.7959 23.7841 12.8724 23.187 12.1336C21.976 10.6307 20.1596 9.85825 17.7751 9.84146C15.4113 9.86245 13.624 10.6307 12.4587 12.1252C11.3721 13.5231 10.8081 15.5465 10.7874 18.1366C10.8081 20.7268 11.3721 22.7502 12.4587 24.1523C13.6198 25.6468 15.4113 26.415 17.7751 26.436C19.9067 26.4192 21.3167 25.9071 22.4903 24.7191C23.8297 23.3673 23.8049 21.7049 23.3777 20.6932C23.1248 20.0971 22.6686 19.6017 22.0548 19.2281C21.9014 20.3574 21.5655 21.2557 21.0305 21.9484C20.3214 22.8636 19.3137 23.3589 18.0156 23.4303C17.0369 23.4849 16.0956 23.2456 15.3657 22.7586C14.5031 22.1793 13.9972 21.2977 13.9433 20.2692C13.8396 18.2416 15.4238 16.7849 17.8912 16.6422C18.7662 16.5918 19.5874 16.6296 20.3463 16.7597C20.2467 16.1384 20.0435 15.6431 19.7408 15.282C19.3261 14.7909 18.6792 14.539 17.8249 14.5348H17.7958C17.1074 14.5348 16.1785 14.7279 15.5855 15.6389L14.1589 14.6481C14.9552 13.4265 16.2449 12.7549 17.8 12.7549H17.8332C20.4292 12.7716 21.976 14.413 22.1336 17.2761L22.1253 17.2845H22.1295ZM15.6601 20.1727C15.7141 21.2263 16.8379 21.7175 17.9244 21.6545C18.986 21.5958 20.1887 21.176 20.3919 18.5816C19.8445 18.4599 19.239 18.3969 18.5921 18.3969C18.393 18.3969 18.194 18.4011 17.9949 18.4137C16.2158 18.5145 15.6228 19.3876 15.6643 20.1685L15.6601 20.1727Z" fill="white"/>
        </svg>
    ),
    whatsapp: (
        <svg width="40" height="40" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M36 18C36 27.9411 27.9411 36 18 36C8.05887 36 0 27.9411 0 18C0 8.05887 8.05887 0 18 0C27.9411 0 36 8.05887 36 18Z" fill="#5DCA45"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M29.9082 17.6894C29.8994 19.7259 29.361 21.7252 28.3461 23.4909C27.3311 25.2566 25.8744 26.7279 24.119 27.7605C22.3636 28.7931 20.3698 29.3514 18.3334 29.3807C16.297 29.4099 14.288 28.909 12.5037 27.9273L6.00781 30.001L8.12553 23.7332C7.20023 22.1844 6.64496 20.4429 6.50296 18.6444C6.36096 16.8458 6.63604 15.0388 7.30682 13.364C7.9776 11.6892 9.02594 10.1918 10.3702 8.98858C11.7145 7.78532 13.3184 6.90861 15.057 6.42675C16.7956 5.9449 18.6219 5.87091 20.3938 6.21052C22.1657 6.55014 23.8352 7.29421 25.2725 8.38477C26.7097 9.47534 27.8757 10.883 28.6798 12.498C29.4839 14.1131 29.9042 15.8919 29.9082 17.696V17.6894ZM18.1727 7.85823C16.3573 7.84953 14.575 8.34431 13.0238 9.28759C11.4727 10.2309 10.2134 11.5857 9.38588 13.2016C8.55838 14.8175 8.19505 16.6312 8.33629 18.4411C8.47752 20.2511 9.11777 21.9865 10.1859 23.4544L8.95369 27.1045L12.7437 25.8949C14.0234 26.746 15.4846 27.2856 17.0104 27.4704C18.5361 27.6552 20.0839 27.4802 21.5298 26.9593C22.9757 26.4383 24.2795 25.5861 25.3368 24.4706C26.3941 23.3552 27.1755 22.0077 27.6183 20.536C28.0612 19.0643 28.1532 17.5094 27.8871 15.9957C27.621 14.4821 27.0041 13.0518 26.0858 11.8194C25.1674 10.5871 23.9733 9.58688 22.5989 8.89901C21.2246 8.21114 19.7096 7.8547 18.1727 7.85823ZM24.0978 20.3832C24.0258 20.2632 23.8311 20.1912 23.547 20.0485C23.263 19.9058 21.8441 19.211 21.5813 19.115C21.3186 19.0189 21.1253 18.9723 20.9346 19.2577C20.7439 19.5431 20.1918 20.1912 20.0224 20.3819C19.853 20.5726 19.6877 20.5966 19.3996 20.4539C18.5518 20.1177 17.7689 19.6367 17.0858 19.0323C16.4581 18.4523 15.9189 17.7833 15.4856 17.0466C15.3189 16.7612 15.4696 16.6052 15.6109 16.4625C15.7523 16.3198 15.899 16.1278 16.043 15.9611C16.1613 15.8155 16.2583 15.6539 16.331 15.481C16.3692 15.402 16.3869 15.3146 16.3825 15.227C16.3781 15.1394 16.3517 15.0543 16.3057 14.9795C16.235 14.8368 15.6589 13.4259 15.4189 12.8458C15.1788 12.2657 14.9401 12.295 14.7721 12.295C14.6121 12.3022 14.4527 12.3187 14.2947 12.3444C13.986 12.3809 13.6944 12.5054 13.4545 12.7031C13.1299 13.0096 12.8728 13.3804 12.6996 13.7919C12.5265 14.2034 12.4411 14.6465 12.449 15.0929C12.5428 16.174 12.9507 17.204 13.6225 18.0561C14.8187 19.9341 16.5226 21.4346 18.5368 22.3836C21.4613 23.5318 21.4613 23.1477 21.9881 23.0997C22.3894 23.0223 22.7698 22.861 23.1044 22.6263C23.439 22.3917 23.7203 22.089 23.9298 21.7381C24.1175 21.3104 24.1759 20.8371 24.0978 20.3765V20.3832Z" fill="white"/>
        </svg>
    ),
    tiktok: (
        <svg width="40" height="40" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M36 18C36 27.9411 27.9411 36 18 36C8.05887 36 0 27.9411 0 18C0 8.05887 8.05887 0 18 0C27.9411 0 36 8.05887 36 18Z" fill="black"/>
            <path d="M25.1941 16.6872C23.8072 16.6901 22.4544 16.2572 21.3269 15.4496V21.0802C21.3266 22.123 21.0079 23.1409 20.4134 23.9977C19.8189 24.8545 18.977 25.5093 18.0003 25.8746C17.0235 26.24 15.9585 26.2983 14.9477 26.042C13.9369 25.7856 13.0284 25.2268 12.3438 24.4401C11.6592 23.6535 11.2311 22.6766 11.1168 21.64C11.0025 20.6035 11.2074 19.5568 11.704 18.6398C12.2007 17.7229 12.9655 16.9794 13.8962 16.5089C14.8268 16.0384 15.879 15.8633 16.9118 16.007V18.838C16.4395 18.6894 15.9324 18.6937 15.4627 18.8505C14.993 19.0072 14.5849 19.3083 14.2966 19.7109C14.0082 20.1134 13.8545 20.5967 13.8572 21.0919C13.86 21.587 14.0191 22.0686 14.3118 22.4679C14.6046 22.8672 15.016 23.1638 15.4874 23.3154C15.9587 23.4669 16.466 23.4657 16.9366 23.3118C17.4072 23.1579 17.8171 22.8593 18.1079 22.4585C18.3987 22.0577 18.5554 21.5753 18.5557 21.0802V10.0771H21.3269C21.3254 10.3115 21.3454 10.5456 21.3868 10.7763C21.4831 11.2905 21.6834 11.7797 21.9752 12.214C22.267 12.6482 22.6444 13.0184 23.0841 13.3019C23.7101 13.7153 24.4439 13.9354 25.1941 13.9348V16.6872Z" fill="white"/>
        </svg>
    ),
};

const copyToClipboard = async (text) => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) return false;
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Error copying text: ', err);
        return false;
    }
};

const getImageExtension = (image_link) =>
    (image_link.split('.').pop().split('?')[0] || 'jpg').toLowerCase();

const downloadImage = async (image_link) => {
    try {
        const sourceUrl = cdnImage(image_link, { width: 1600 });
        const response = await fetch(sourceUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `image.${getImageExtension(image_link)}`);
        document.body.appendChild(a);
        a.click();
        a.parentNode.removeChild(a);
        return true;
    } catch (err) {
        console.error('Error downloading the image ', err);
        return false;
    }
};

const isMobile = () =>
    typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Instagram and TikTok captions don't render URLs as clickable links, so we
// omit the URL from the caption for those platforms. They lose click tracking
// in exchange for cleaner captions.
const PLATFORMS_WITHOUT_URL = new Set(['instagram', 'tiktok']);

const PlatformShareButton = ({ platform, image_link, description, post_url, post_id, track }) => {
    const [isDesktopOverlayOpen, setIsDesktopOverlayOpen] = useState(false);
    const [isMobileFallbackOpen, setIsMobileFallbackOpen] = useState(false);
    const [imageDownloaded, setImageDownloaded] = useState(false);
    const [view, setView] = useState('instructions');
    const [whatsappShareUrl, setWhatsappShareUrl] = useState(null);

    const closeDesktopOverlay = () => {
        setIsDesktopOverlayOpen(false);
        setView('instructions');
        setWhatsappShareUrl(null);
    };
    const closeMobileFallback = () => {
        setIsMobileFallbackOpen(false);
        setView('instructions');
    };
    const claimReward = () => {
        window.open(REWARD_URL, '_blank');
    };

    if (!PLATFORM_LABELS[platform]) return null;

    // Build the legacy share URL (post_url already points at /s/<postId>) and
    // append ?p=<platform> so the existing onRequestUrl path stays attribution-aware.
    const legacyShareUrl = (() => {
        try {
            const u = new URL(post_url);
            u.searchParams.set('p', platform);
            return u.toString();
        } catch {
            return post_url;
        }
    })();

    const buildCopyText = (shareUrl) =>
        PLATFORMS_WITHOUT_URL.has(platform) ? description : `${description} ${shareUrl}`;

    const resolveShareUrl = async () => {
        if (!post_id) return legacyShareUrl;
        try {
            const memberId = readMemberId();
            const code = await mintShortLink({ postId: post_id, platform, memberId, campaign: track });
            if (!code) return legacyShareUrl;
            const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
                ? window.location.origin
                : '';
            return `${origin}/l/${code}`;
        } catch (err) {
            console.error('Short link mint failed, falling back to legacy URL', err);
            return legacyShareUrl;
        }
    };

    const platformDisplay = PLATFORM_DISPLAY_NAME[platform] || platform;
    const composeUrl = PLATFORM_COMPOSE_URL[platform];

    const handleShare = async () => {
        if (platform === 'whatsapp') {
            // Two-click flow so we can mint a /l/<code> short link with member
            // attribution without losing the iOS Safari user gesture:
            //   1. This click opens the prep modal synchronously (no popup, so
            //      no gesture needed). mintShortLink runs in the background.
            //   2. The modal's "Open WhatsApp" button is a fresh user gesture,
            //      which iOS honors for window.open even though we've awaited
            //      the mint in between.
            // Seed whatsappShareUrl with a legacy /s/<id>?p=whatsapp&m=<id>
            // URL so an impatient tap on the CTA before the mint resolves
            // still carries the member id through onRequestUrl.
            const memberId = readMemberId();
            const legacyWithMember = (() => {
                if (!memberId) return legacyShareUrl;
                try {
                    const u = new URL(legacyShareUrl);
                    u.searchParams.set('m', memberId);
                    return u.toString();
                } catch {
                    return legacyShareUrl;
                }
            })();
            setWhatsappShareUrl(legacyWithMember);
            setView('whatsapp-prep');
            setIsDesktopOverlayOpen(true);

            if (post_id) {
                mintShortLink({ postId: post_id, platform, memberId, campaign: track })
                    .then((code) => {
                        if (!code || typeof window === 'undefined') return;
                        setWhatsappShareUrl(`${window.location.origin}/l/${code}`);
                    })
                    .catch((err) => console.error('WhatsApp short link mint failed, using legacy', err));
            }
            return;
        }

        if (isMobile()) {
            // Two-tier write so iOS Safari's gesture-bound clipboard plays nice
            // with our async mintShortLink:
            //   1. ClipboardItem with a Promise — the gesture validates at
            //      clipboard.write() but the Blob resolves after we've awaited
            //      mintShortLink, so we get the short /l/<code> URL with full
            //      member attribution via the links/<code>.memberId field.
            //   2. Fallback to the synchronous /s/<id>?p=...&m=<memberId> URL
            //      when ClipboardItem isn't available (in-app webviews etc.).
            //      onRequestUrl reads ?m= and emits utm_term, so attribution
            //      still survives.
            const memberId = readMemberId();
            const fallbackUrl = (() => {
                if (!memberId) return legacyShareUrl;
                try {
                    const u = new URL(legacyShareUrl);
                    u.searchParams.set('m', memberId);
                    return u.toString();
                } catch {
                    return legacyShareUrl;
                }
            })();

            let copied = false;
            if (typeof ClipboardItem !== 'undefined' && navigator.clipboard && navigator.clipboard.write) {
                try {
                    const blobPromise = (async () => {
                        const code = await mintShortLink({ postId: post_id, platform, memberId, campaign: track });
                        const shareUrl = code
                            ? `${window.location.origin}/l/${code}`
                            : fallbackUrl;
                        return new Blob([buildCopyText(shareUrl)], { type: 'text/plain' });
                    })();
                    await navigator.clipboard.write([
                        new ClipboardItem({ 'text/plain': blobPromise }),
                    ]);
                    copied = true;
                } catch (err) {
                    console.warn('Async clipboard write failed, falling back', err);
                }
            }

            if (!copied) {
                await copyToClipboard(buildCopyText(fallbackUrl));
            }

            setIsMobileFallbackOpen(true);
            return;
        }

        const shareUrl = await resolveShareUrl();
        const copyText = buildCopyText(shareUrl);
        await copyToClipboard(copyText);
        const downloaded = await downloadImage(image_link);
        setImageDownloaded(downloaded);
        setIsDesktopOverlayOpen(true);
    };

    const openComposeUrl = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (composeUrl) window.open(composeUrl, '_blank');
        markShared();
        setView('reward');
    };

    const openWhatsApp = () => {
        const url = whatsappShareUrl || legacyShareUrl;
        const copyText = buildCopyText(url);
        window.open(`https://wa.me/?text=${encodeURIComponent(copyText)}`, '_blank');
        markShared();
        setView('reward');
    };

    const label = PLATFORM_LABELS[platform];
    const icon = PLATFORM_ICONS[platform];

    return (
        <>
            <button
                type="button"
                onClick={handleShare}
                title={label}
                aria-label={label}
                className="relative h-12 w-12 flex-shrink-0 flex items-center justify-center bg-transparent border-none rounded-full transition-transform duration-200 hover:scale-110 focus:outline-none"
            >
                {icon}
            </button>

            {isDesktopOverlayOpen && createPortal((
                <div
                    className="modal-backdrop fixed inset-0 z-[1000] flex items-center justify-center px-4 bg-brand-ink/75 backdrop-blur-sm"
                    onClick={closeDesktopOverlay}
                    role="dialog"
                    aria-modal="true"
                >
                    {view === 'reward' && <Confetti />}
                    <div
                        className="modal-card relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full text-brand-muted hover:text-brand-ink hover:bg-brand-bg transition-colors"
                            onClick={closeDesktopOverlay}
                            aria-label="Close"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="6" y1="6" x2="18" y2="18" />
                                <line x1="18" y1="6" x2="6" y2="18" />
                            </svg>
                        </button>

                        <div className="p-7 sm:p-8">
                            {view === 'reward' ? (
                                <>
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                        You earned it
                                    </div>
                                    <h3 className="mt-1.5 font-brand text-2xl sm:text-[28px] font-bold text-brand-ink leading-tight">
                                        Free student ticket<span className="text-brand-red">.</span>
                                    </h3>
                                    <p className="mt-4 text-sm text-brand-ink leading-relaxed">
                                        Use coupon <strong className="font-semibold">{REWARD_COUPON}</strong> at checkout. For @edu email addresses only.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={claimReward}
                                        className="group mt-6 w-full bg-brand-ink hover:bg-black text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                                    >
                                        Claim ticket
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </button>
                                </>
                            ) : view === 'whatsapp-prep' ? (
                                <>
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                        Ready to send
                                    </div>
                                    <h3 className="mt-1.5 font-brand text-2xl sm:text-[28px] font-bold text-brand-ink leading-tight">
                                        Send via WhatsApp<span className="text-brand-red">.</span>
                                    </h3>
                                    <p className="mt-4 text-sm text-brand-ink leading-relaxed">
                                        Caption and link prefilled. Pick a chat in WhatsApp and tap send.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={openWhatsApp}
                                        className="group mt-6 w-full bg-brand-ink hover:bg-black text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                                    >
                                        Open WhatsApp
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                        Ready to post
                                    </div>
                                    <h3 className="mt-1.5 font-brand text-2xl sm:text-[28px] font-bold text-brand-ink leading-tight">
                                        Finish on {platformDisplay}<span className="text-brand-red">.</span>
                                    </h3>

                                    <div className="mt-5 space-y-3 text-sm text-brand-ink">
                                        {imageDownloaded ? (
                                            <div className="flex items-center gap-2.5">
                                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                        <polyline points="5 12 10 17 19 7" />
                                                    </svg>
                                                </span>
                                                <span>Image saved to your downloads</span>
                                            </div>
                                        ) : (
                                            <div className="rounded-xl border border-brand-panel p-3 bg-brand-bg">
                                                <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-brand-muted mb-2">
                                                    Save this image
                                                </div>
                                                <img
                                                    src={cdnImage(image_link, { width: 900 })}
                                                    alt=""
                                                    className="w-full max-h-44 object-contain rounded-lg bg-white"
                                                />
                                                <div className="mt-2 text-xs text-brand-muted">
                                                    Right-click the image and choose <strong className="text-brand-ink">Save Image As</strong>.
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2.5">
                                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center">
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <polyline points="5 12 10 17 19 7" />
                                                </svg>
                                            </span>
                                            <span>Caption copied to clipboard</span>
                                        </div>
                                    </div>

                                    <ol className="mt-6 space-y-3.5">
                                        <li className="flex gap-3.5">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-ink text-white text-[12px] font-semibold flex items-center justify-center">1</span>
                                            <span className="text-sm text-brand-ink pt-0.5 leading-snug">Open {platformDisplay}</span>
                                        </li>
                                        <li className="flex gap-3.5">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-ink text-white text-[12px] font-semibold flex items-center justify-center">2</span>
                                            <span className="text-sm text-brand-ink pt-0.5 leading-snug">Start a new post and attach the downloaded image</span>
                                        </li>
                                        <li className="flex gap-3.5">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-ink text-white text-[12px] font-semibold flex items-center justify-center">3</span>
                                            <span className="text-sm text-brand-ink pt-0.5 leading-snug">Paste the caption</span>
                                        </li>
                                    </ol>

                                    <button
                                        type="button"
                                        onClick={openComposeUrl}
                                        className="group mt-7 w-full bg-brand-ink hover:bg-black text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                                    >
                                        Open {platformDisplay} &amp; paste
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ), document.body)}

            {isMobileFallbackOpen && createPortal((
                <div
                    className="modal-backdrop fixed inset-0 z-[1000] bg-brand-ink/80 backdrop-blur-sm overflow-y-auto"
                    role="dialog"
                    aria-modal="true"
                    onClick={closeMobileFallback}
                >
                    {view === 'reward' && <Confetti />}
                    <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-6">
                        <div
                            className="modal-card relative w-full max-w-md bg-white rounded-2xl shadow-2xl my-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white text-brand-muted hover:text-brand-ink hover:bg-brand-bg transition-colors"
                                onClick={closeMobileFallback}
                                aria-label="Close"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                </svg>
                            </button>

                            <div className="p-6">
                                {view === 'reward' ? (
                                    <>
                                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                            You earned it
                                        </div>
                                        <h3 className="mt-1.5 font-brand text-xl font-bold text-brand-ink leading-tight">
                                            Free student ticket<span className="text-brand-red">.</span>
                                        </h3>
                                        <p className="mt-3 text-sm text-brand-ink leading-relaxed">
                                            Use coupon <strong className="font-semibold">{REWARD_COUPON}</strong> at checkout. For @edu email addresses only.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={claimReward}
                                            className="group mt-5 w-full bg-brand-ink hover:bg-black text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                                        >
                                            Claim ticket
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                            Step 1
                                        </div>
                                        <h3 className="mt-1.5 font-brand text-xl font-bold text-brand-ink leading-tight">
                                            Save the image
                                        </h3>
                                        <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                                            Long-press the image and choose <strong className="text-brand-ink">Save to Photos</strong> (iPhone) or <strong className="text-brand-ink">Download image</strong> (Android).
                                        </p>
                                        <img
                                            src={cdnImage(image_link, { width: 900 })}
                                            alt=""
                                            className="mt-4 w-full max-h-[35vh] rounded-xl object-contain bg-brand-bg"
                                        />
                                        <div className="mt-3 flex items-center gap-2 text-xs text-brand-muted">
                                            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center">
                                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <polyline points="5 12 10 17 19 7" />
                                                </svg>
                                            </span>
                                            Caption already copied
                                        </div>

                                        <hr className="my-6 border-brand-panel" />

                                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                            Step 2
                                        </div>
                                        <h3 className="mt-1.5 font-brand text-xl font-bold text-brand-ink leading-tight">
                                            Publish on {platformDisplay}
                                        </h3>
                                        <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                                            Create a new post, attach the saved image, and paste the caption.
                                        </p>

                                        <button
                                            type="button"
                                            className="group mt-5 w-full bg-brand-ink hover:bg-black text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                                            onClick={openComposeUrl}
                                        >
                                            Open {platformDisplay} &amp; paste
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ), document.body)}
        </>
    );
};

export default PlatformShareButton;
